import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'companies';

// Get company by slug (for tenant resolution)
export const getCompanyBySlug = async (slug) => {
  try {
    const normalizedSlug = slug.toLowerCase();
    
    const q = query(
      collection(db, COLLECTION_NAME),
      where('slug', '==', normalizedSlug)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { company: null, error: null };
    }
    
    const doc = querySnapshot.docs[0];
    return { 
      company: { id: doc.id, ...doc.data() }, 
      error: null 
    };
  } catch (error) {
    console.error('Error getting company by slug:', error);
    return { company: null, error: error.message };
  }
};

// Get company by ID
export const getCompanyById = async (companyId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, companyId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { 
        company: { id: docSnap.id, ...docSnap.data() }, 
        error: null 
      };
    }
    return { company: null, error: 'Company not found' };
  } catch (error) {
    console.error('Error getting company:', error);
    return { company: null, error: error.message };
  }
};

// Get all companies (admin only) - excludes deleted companies
export const getAllCompanies = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const companies = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Filter out deleted companies
      if (data.status !== 'deleted') {
        companies.push({ id: doc.id, ...data });
      }
    });
    
    return { companies, error: null };
  } catch (error) {
    console.error('Error getting all companies:', error);
    return { companies: [], error: error.message };
  }
};

// Get active companies (for company selector)
export const getActiveCompanies = async () => {
  try {
    // Simple query without orderBy to avoid composite index requirement
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', '==', 'active')
    );
    const querySnapshot = await getDocs(q);
    
    const companies = [];
    querySnapshot.forEach((doc) => {
      companies.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort in memory
    return { 
      companies: companies.sort((a, b) => (a.name || '').localeCompare(b.name || '')), 
      error: null 
    };
  } catch (error) {
    console.error('Error getting active companies:', error);
    // Return empty array if collection doesn't exist yet
    return { companies: [], error: null };
  }
};

// Create company
export const createCompany = async (data) => {
  try {
    // Validate slug is unique
    const existingCompany = await getCompanyBySlug(data.slug);
    if (existingCompany.company) {
      return { id: null, error: 'This slug is already taken. Please choose a different one.' };
    }
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...data,
      slug: data.slug.toLowerCase().replace(/[^a-z0-9-]/g, ''),
      status: data.status || 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { id: docRef.id, error: null };
  } catch (error) {
    console.error('Error creating company:', error);
    return { id: null, error: error.message };
  }
};

// Update company
export const updateCompany = async (companyId, data) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, companyId);
    
    // If slug is being updated, check uniqueness
    if (data.slug) {
      const existingCompany = await getCompanyBySlug(data.slug);
      if (existingCompany.company && existingCompany.company.id !== companyId) {
        return { error: 'This slug is already taken. Please choose a different one.' };
      }
      data.slug = data.slug.toLowerCase().replace(/[^a-z0-9-]/g, '');
    }
    
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    
    return { error: null };
  } catch (error) {
    console.error('Error updating company:', error);
    return { error: error.message };
  }
};

// Toggle company status
export const toggleCompanyStatus = async (companyId, status) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, companyId);
    await updateDoc(docRef, {
      status,
      updatedAt: serverTimestamp()
    });
    return { error: null };
  } catch (error) {
    console.error('Error toggling company status:', error);
    return { error: error.message };
  }
};

// Delete company (hard delete - removes document from Firestore)
export const deleteCompany = async (companyId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, companyId);
    await deleteDoc(docRef);
    return { error: null };
  } catch (error) {
    console.error('Error deleting company:', error);
    return { error: error.message };
  }
};

// Check slug availability
export const isSlugAvailable = async (slug, excludeCompanyId = null) => {
  try {
    const normalizedSlug = slug.toLowerCase().replace(/[^a-z0-9-]/g, '');
    const existingCompany = await getCompanyBySlug(normalizedSlug);
    
    if (!existingCompany.company) {
      return { available: true, error: null };
    }
    
    // If we're checking for an update, exclude the current company
    if (excludeCompanyId && existingCompany.company.id === excludeCompanyId) {
      return { available: true, error: null };
    }
    
    return { available: false, error: null };
  } catch (error) {
    console.error('Error checking slug availability:', error);
    return { available: false, error: error.message };
  }
};

// Get company stats (for admin dashboard)
export const getCompanyStats = async () => {
  try {
    const allCompanies = await getAllCompanies();
    
    const stats = {
      total: allCompanies.companies.length,
      active: allCompanies.companies.filter(c => c.status === 'active').length,
      inactive: allCompanies.companies.filter(c => c.status === 'inactive').length,
    };
    
    return { stats, error: null };
  } catch (error) {
    console.error('Error getting company stats:', error);
    return { stats: null, error: error.message };
  }
};
